import { createSignal, onMount, onCleanup, For, Switch, Match } from "solid-js";
import Text from "../components/Text";
import { useApi, isOk } from "../api";
import type { ModJarInfoDto } from "../api/generated/schemas";
import styles from "./Koffermatic.module.css";

const FEATURES: [string, string, string][] = [
    ["01", "KOFFERMATIC_F_PRINT_TITLE", "KOFFERMATIC_F_PRINT_DESC"],
    ["02", "KOFFERMATIC_F_GHOST_TITLE", "KOFFERMATIC_F_GHOST_DESC"],
    ["03", "KOFFERMATIC_F_SURVIVAL_TITLE", "KOFFERMATIC_F_SURVIVAL_DESC"],
    ["04", "KOFFERMATIC_F_LOCAL_TITLE", "KOFFERMATIC_F_LOCAL_DESC"],
];

const STEPS: [string, string][] = [
    ["KOFFERMATIC_STEP1_TITLE", "KOFFERMATIC_STEP1_DESC"],
    ["KOFFERMATIC_STEP2_TITLE", "KOFFERMATIC_STEP2_DESC"],
    ["KOFFERMATIC_STEP3_TITLE", "KOFFERMATIC_STEP3_DESC"],
    ["KOFFERMATIC_STEP4_TITLE", "KOFFERMATIC_STEP4_DESC"],
];

const Underline = () => (
    <svg viewBox="0 0 300 16" preserveAspectRatio="none">
        <path d="M3 11 C 60 3, 120 15, 185 8 S 282 5, 297 10" fill="none" stroke="#d1442f" stroke-width="4" stroke-linecap="round" />
    </svg>
);

type LoadState = "loading" | "ready" | "error";

export default function Koffermatic() {
    const api = useApi();
    const [info, setInfo] = createSignal<ModJarInfoDto | null>(null);
    const [state, setState] = createSignal<LoadState>("loading");
    let setupRef: HTMLElement | undefined;

    const load = async () => {
        setState("loading");
        try {
            const res = await api.koffermatic.info();
            if (isOk(res.data) && res.data.value) {
                setInfo(res.data.value as ModJarInfoDto);
                setState("ready");
            } else {
                setState("error");
            }
        } catch {
            setState("error");
        }
    };

    onMount(() => {
        const previousTitle = document.title;
        document.title = "Koffermatic";

        const previousIcons = Array.from(document.head.querySelectorAll('link[rel~="icon"]'));
        previousIcons.forEach((icon) => icon.remove());
        const icon = document.createElement("link");
        icon.rel = "icon";
        icon.type = "image/svg+xml";
        icon.href = "/koffermatic.svg";
        document.head.appendChild(icon);

        onCleanup(() => {
            document.title = previousTitle;
            icon.remove();
            previousIcons.forEach((prev) => document.head.appendChild(prev));
        });

        void load();
    });

    const version = () => info()?.version ?? "1.0.0";

    const downloadButton = () => (
        <Switch>
            <Match when={state() === "loading"}>
                <span class={styles.dlState}>
                    <i class="fa-solid fa-spinner fa-spin" />
                    <Text val="KOFFERMATIC_LOADING" />
                </span>
            </Match>
            <Match when={state() === "error"}>
                <button class={`${styles.dlState} ${styles.dlError}`} onClick={() => void load()}>
                    <i class="fa-solid fa-triangle-exclamation" />
                    <Text val="KOFFERMATIC_LOAD_ERROR" />
                </button>
            </Match>
            <Match when={state() === "ready" && !info()?.available}>
                <span class={styles.dlState}>
                    <i class="fa-solid fa-hourglass-half" />
                    <Text val="KOFFERMATIC_NO_BUILD" />
                </span>
            </Match>
            <Match when={state() === "ready" && info()?.available}>
                <a class={styles.btn} href="/api/koffermatic/download" target="_blank" rel="external noopener">
                    <i class="fa-solid fa-download" />
                    <Text val="KOFFERMATIC_DOWNLOAD" />
                    <span class={styles.btnVer}>v{info()!.version}</span>
                </a>
            </Match>
        </Switch>
    );

    return (
        <div class={styles.page}>
            <div class={styles.wrap}>
                <div class={styles.top}>
                    <span class={styles.brand}>KOFFERMATIC</span>
                    <span class={styles.mid}>
                        <span><Text val="KOFFERMATIC_TOPBAR_TAG" /></span>
                        <span>Forge 1.8.9 · Windows</span>
                    </span>
                    <span>v{version()}</span>
                </div>

                <section class={styles.hero}>
                    <div>
                        <span class={styles.eyebrow}>◳ <Text val="KOFFERMATIC_EYEBROW" /></span>
                        <h1 class={styles.title}>
                            <Text val="KOFFERMATIC_HERO_LINE1" />{" "}
                            <span class={styles.u}><Text val="KOFFERMATIC_HERO_ACCENT" /><Underline /></span>
                        </h1>
                        <p class={styles.sub}><Text val="KOFFERMATIC_HERO_SUB" /></p>
                        <div class={styles.actions}>
                            {downloadButton()}
                            <a
                                class={styles.ghost}
                                href="#koffermatic-setup"
                                onClick={(e) => { e.preventDefault(); setupRef?.scrollIntoView({ behavior: "smooth" }); }}
                            >
                                <Text val="KOFFERMATIC_HOWTO" /> ↓
                            </a>
                        </div>
                    </div>
                    <div class={styles.art}>
                        <div class={styles.sketch}>
                            <div class={`${styles.cube} ${styles.cProj}`} />
                            <div class={`${styles.cube} ${styles.cReal}`} />
                        </div>
                        <span class={styles.note}><Text val="KOFFERMATIC_ART_NOTE" /></span>
                    </div>
                </section>

                <section class={styles.cols}>
                    <For each={FEATURES}>{([no, title, desc]) => (
                        <div class={styles.col}>
                            <div class={styles.colNo}>{no}</div>
                            <h3 class={styles.colTitle}><Text val={title} /></h3>
                            <p class={styles.colDesc}><Text val={desc} /></p>
                        </div>
                    )}</For>
                </section>

                <section class={styles.sec} ref={setupRef} id="koffermatic-setup">
                    <div class={styles.kick}><Text val="KOFFERMATIC_SETUP_LABEL" /></div>
                    <h2 class={styles.secTitle}><Text val="KOFFERMATIC_SETUP_TITLE" /></h2>
                    <div class={styles.steps}>
                        <For each={STEPS}>{([title, desc], i) => (
                            <div class={styles.step}>
                                <div class={styles.stepNo}>{`0${i() + 1}`}</div>
                                <div class={styles.stepLine} />
                                <h4 class={styles.stepTitle}><Text val={title} /></h4>
                                <p class={styles.stepDesc}><Text val={desc} /></p>
                            </div>
                        )}</For>
                    </div>
                </section>

                <section class={styles.band}>
                    <div>
                        <h2 class={styles.bandTitle}><Text val="KOFFERMATIC_CTA_TITLE" /></h2>
                        <div class={styles.meta}><Text val="KOFFERMATIC_META" /></div>
                    </div>
                    {downloadButton()}
                </section>
            </div>
        </div>
    );
}
